"use client";
import { useForm } from "react-hook-form";
import { useState } from "react";
import DOMPurify from "dompurify";


function InputField({ label, required, type = "text", placeholder, register, name, error }) {
  return (
    <div className="flex flex-col space-y-1 w-full">
      <label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <input
        {...register}
        type={type}
        placeholder={placeholder}
        className={`border rounded-md px-3 py-2 focus:outline-none focus:ring-1
        ${error ? "border-red-500 focus:ring-red-500" : "border-gray-400 focus:ring-red-700"}`}
      />

      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
}

function TextareaField({ label, required, placeholder, register, name, error }) {
  return (
    <div className="flex flex-col space-y-1 w-full">
      <label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <textarea
        {...register}
        rows={4}
        placeholder={placeholder}
        className={`border rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1
        ${error ? "border-red-500 focus:ring-red-500" : "border-gray-400 focus:ring-red-700"}`}
      />

      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
}


export default function ContactForm() {
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Format phone number to +254 format
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Handle different formats
    if (cleaned.startsWith('0')) {
      // 0703698937 -> +254703698937
      return '+254' + cleaned.substring(1);
    } else if (cleaned.startsWith('254')) {
      // 254703698937 -> +254703698937
      return '+' + cleaned;
    } else if (cleaned.length === 9) {
      // 703698937 -> +254703698937
      return '+254' + cleaned;
    }
    
    return '+254' + cleaned;
  };
  
  const onSubmit = async (data) => {
    try {
      setSubmitStatus(null);

      // Format phone number first
      const formattedPhone = formatPhoneNumber(data.phone);

      // Sanitize all inputs before sending
      const sanitizedData = {
        email: DOMPurify.sanitize(data.email.trim()),
        phone: DOMPurify.sanitize(formattedPhone),
        firstName: DOMPurify.sanitize(data.firstName.trim()),
        lastName: DOMPurify.sanitize(data.lastName.trim()),
        message: DOMPurify.sanitize(data.message.trim()),
      };
      
      console.log("Sanitized Payload:", sanitizedData);
      
      // Get the correct API URL
      const apiUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:5000';
      
      // Send to backend
      const response = await fetch(`${apiUrl}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
      });

      const result = await response.json();
      
      console.log('Backend Response:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Something went wrong');
      }

      // Success
      setSubmitStatus({
        type: 'success',
        message: result.message || 'Message sent successfully! We will get back to you soon.'
      });

      // Clear the form
      reset();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to send message. Please try again.'
      });

      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <div className="w-full p-6 md:p-12 rounded-lg">
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        
        {/* Status Message */}
        {submitStatus && (
          <div className={`p-4 rounded-md transition-all ${
            submitStatus.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <p className="font-medium">{submitStatus.message}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">

          <InputField
            label="Work Email"
            name="email"
            type="email"
            placeholder="you@email.com"
            required
            register={register("email", {
              required: "Email is required",
              pattern: {
                value: /^\S+@\S+$/i,
                message: "Invalid email address",
              },
            })}
            error={errors.email}
          />

          <InputField
            label="Phone Number"
            required
            name="phone"
            placeholder="0703698937 or 703698937"
            register={register("phone", {
              required: "Phone number is required",
              validate: (value) => {
                const cleaned = value.replace(/\D/g, '');
                if (cleaned.length < 9 || cleaned.length > 12) {
                  return "Phone number must be 9-10 digits";
                }
                return true;
              }
            })}
            error={errors.phone}
          />

          <InputField
            label="First Name"
            name="firstName"
            required
            placeholder="First name"
            register={register("firstName", {
              required: "First name is required",
              pattern: {
                value: /^[a-zA-Z\s'-]+$/,
                message: "Only letters, spaces, hyphens and apostrophes allowed"
              },
              minLength: {
                value: 2,
                message: "First name must be at least 2 characters"
              },
              maxLength: {
                value: 50,
                message: "First name must be less than 50 characters"
              }
            })}
            error={errors.firstName}
          />

          <InputField
            label="Last Name"
            name="lastName"
            required
            placeholder="Last name"
            register={register("lastName", {
              required: "Last name is required",
              pattern: {
                value: /^[a-zA-Z\s'-]+$/,
                message: "Only letters, spaces, hyphens and apostrophes allowed"
              },
              minLength: {
                value: 2,
                message: "Last name must be at least 2 characters"
              },
              maxLength: {
                value: 50,
                message: "Last name must be less than 50 characters"
              }
            })}
            error={errors.lastName}
          />
        </div>

        <TextareaField
          label="Your Message"
          name="message"
          required
          placeholder="Please enter your message here..."
          register={register("message", {
            required: "Message cannot be empty",
            minLength: {
              value: 10,
              message: "Message should have at least 10 characters"
            },
            maxLength: {
              value: 1000,
              message: "Message must be less than 1000 characters"
            },
            validate: {
              noScripts: (value) => 
                !/<script|javascript:|onerror=/i.test(value) || 
                "Invalid characters detected"
            }
          })}
          error={errors.message}
        />

        <button
          type="submit"
          className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-md flex items-center justify-center transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
          ) : "Send Message"}
        </button>
      </form>
    </div>
  );
}